/**
 * J1 · Pitch-detection engine.
 *
 * McLeod Pitch Method (normalized square difference function) with explicit
 * octave-error handling: instead of taking the globally highest NSDF peak we
 * accept the *first* key maximum whose height clears `PEAK_TOLERANCE` × the
 * highest peak — the standard defense against picking a harmonic an octave up.
 * Designed to run on-device (browser AudioWorklet / main thread) on ~2048
 * sample windows, robust enough for phone mics and untrained voices: a
 * clarity threshold rejects noise, and a level gate rejects silence.
 */
import { freqToMidi } from './music.js';
export const DEFAULT_CLARITY_THRESHOLD = 0.8;
export const DEFAULT_RMS_GATE = 0.008;
export const PITCH_MIN_FREQ = 55; // A1 — below any usable vocal fundamental
export const PITCH_MAX_FREQ = 1500; // above soprano C7 harmonics we care about
export function rmsLevel(buf) {
    let sum = 0;
    for (let i = 0; i < buf.length; i++)
        sum += buf[i] * buf[i];
    return Math.sqrt(sum / buf.length);
}
/** Lag range analyzed for a given window/sample rate (shared by JS + WASM paths). */
export function nsdfLagRange(windowSize, sampleRate) {
    return {
        minLag: Math.max(2, Math.floor(sampleRate / PITCH_MAX_FREQ)),
        maxLag: Math.min(windowSize - 1, Math.floor(sampleRate / PITCH_MIN_FREQ)),
    };
}
/**
 * Reference NSDF: nsdf[tau] = 2 * acf[tau] / m[tau]. The AudioWorklet runs
 * the same loop compiled to WASM (see nsdf-wasm.ts); this JS version is the
 * fallback path and the ground truth the WASM kernel is tested against.
 */
export function computeNsdf(buf, sampleRate) {
    const n = buf.length;
    const { minLag, maxLag } = nsdfLagRange(n, sampleRate);
    const nsdf = new Float32Array(maxLag + 1);
    for (let tau = minLag; tau <= maxLag; tau++) {
        let acf = 0;
        let m = 0;
        for (let i = 0; i < n - tau; i++) {
            const a = buf[i];
            const b = buf[i + tau];
            acf += a * b;
            m += a * a + b * b;
        }
        nsdf[tau] = m > 0 ? (2 * acf) / m : 0;
    }
    return nsdf;
}
/**
 * Peak-picking half of the McLeod method: key maxima between zero crossings,
 * first peak above PEAK_TOLERANCE × highest (octave-error defense), parabolic
 * interpolation for sub-sample lag precision.
 *
 * DELIBERATELY SELF-CONTAINED (no outer references): the AudioWorklet embeds
 * this function's source via `Function.prototype.toString()`, so it must not
 * close over module constants or helpers.
 */
export function detectPitchFromNsdf(nsdf, minLag, maxLag, sampleRate, clarityThreshold) {
    const PEAK_TOLERANCE = 0.9;
    const peaks = [];
    let inPositive = false;
    let bestLag = 0;
    let bestVal = -Infinity;
    for (let tau = minLag + 1; tau <= maxLag; tau++) {
        const v = nsdf[tau];
        if (!inPositive && v > 0 && nsdf[tau - 1] <= 0) {
            inPositive = true;
            bestLag = tau;
            bestVal = v;
        }
        else if (inPositive) {
            if (v > bestVal) {
                bestVal = v;
                bestLag = tau;
            }
            if (v <= 0) {
                peaks.push({ lag: bestLag, value: bestVal });
                inPositive = false;
            }
        }
    }
    if (inPositive)
        peaks.push({ lag: bestLag, value: bestVal });
    if (peaks.length === 0)
        return null;
    let highest = -Infinity;
    for (const p of peaks)
        if (p.value > highest)
            highest = p.value;
    if (highest < clarityThreshold)
        return null;
    const threshold = PEAK_TOLERANCE * highest;
    let chosen = peaks[0];
    for (const p of peaks) {
        if (p.value >= threshold) {
            chosen = p;
            break;
        }
    }
    let lag = chosen.lag;
    if (lag > minLag && lag < maxLag) {
        const y0 = nsdf[lag - 1];
        const y1 = nsdf[lag];
        const y2 = nsdf[lag + 1];
        const denom = y0 - 2 * y1 + y2;
        if (Math.abs(denom) > 1e-9) {
            const shift = (0.5 * (y0 - y2)) / denom;
            if (Math.abs(shift) < 1)
                lag += shift;
        }
    }
    const freqHz = sampleRate / lag;
    // frequency sanity bounds (PITCH_MIN_FREQ/PITCH_MAX_FREQ, inlined — see note above)
    if (freqHz < 55 || freqHz > 1500)
        return null;
    const midi = 69 + 12 * Math.log2(freqHz / 440);
    return { freqHz, midi, clarity: chosen.value };
}
/**
 * Detect the fundamental of a mono audio window. Returns null when the window
 * is silence/noise (gated by rms + clarity).
 */
export function detectPitch(buf, sampleRate, opts = {}) {
    const clarityThreshold = opts.clarityThreshold ?? DEFAULT_CLARITY_THRESHOLD;
    const rmsGate = opts.rmsGate ?? DEFAULT_RMS_GATE;
    const rms = rmsLevel(buf);
    if (rms < rmsGate)
        return null;
    const { minLag, maxLag } = nsdfLagRange(buf.length, sampleRate);
    const nsdf = computeNsdf(buf, sampleRate);
    const res = detectPitchFromNsdf(nsdf, minLag, maxLag, sampleRate, clarityThreshold);
    if (!res)
        return null;
    return { freqHz: res.freqHz, midi: freqToMidi(res.freqHz), clarity: res.clarity, rms };
}
/**
 * Streaming smoother: median-of-5 over recent detections kills single-frame
 * octave flips and glitches before they reach the UI or the score.
 */
export class PitchSmoother {
    recent = [];
    push(midi) {
        this.recent.push(midi);
        if (this.recent.length > 5)
            this.recent.shift();
        const sorted = [...this.recent].sort((a, b) => a - b);
        return sorted[Math.floor(sorted.length / 2)];
    }
    reset() {
        this.recent = [];
    }
}
//# sourceMappingURL=pitch.js.map